import { AlertCircle, PlusIcon, Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import GradientWrapper from '@/common/GradientWrapper'
import { cn } from '@/theme/utils'
import { UiButton } from '@/ui/UiButton'
import { UiSwitch } from '@/ui/UiSwitch'
import {
  UiTooltip,
  UiTooltipContent,
  UiTooltipProvider,
  UiTooltipTrigger,
} from '@/ui/UiTooltip'

export function NftListHeader({
  searchQuery,
  onSearchChange,
  showMineOnly,
  onToggleMineOnly,
  isWalletConnected,
  isModuleAdmin,
  isValidator,
  isJailed,
  onMintOpen,
  onBecomeOpen,
  onUnjailOpen,
}: {
  searchQuery: string
  onSearchChange: (value: string) => void
  showMineOnly: boolean
  onToggleMineOnly: () => void
  isWalletConnected: boolean
  isModuleAdmin: boolean
  isValidator: boolean
  isJailed: boolean
  onMintOpen: () => void
  onBecomeOpen: () => void
  onUnjailOpen: () => void
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [isStuck, setIsStuck] = useState(false)

  // Detect when the header sticks to the top to toggle background
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        setIsStuck(!entries[0].isIntersecting)
      },
      { threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div ref={sentinelRef} className='h-px w-full' />
      <div
        className={cn(
          'sticky top-0 z-50 mb-4 flex flex-col gap-4 px-4 py-4 transition-all sm:px-0 sm:py-5',
          isStuck && 'bg-black',
        )}
      >
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h1 className='text-foreground text-2xl'>Shards list</h1>
          {isWalletConnected && (
            <div className='flex items-center justify-between gap-2 sm:justify-end'>
              <span className='text-foreground text-xs'>Show only mine</span>
              <UiSwitch
                checked={showMineOnly}
                onCheckedChange={onToggleMineOnly}
              />
            </div>
          )}
        </div>

        <div className='flex flex-col justify-between gap-3 sm:flex-row sm:items-center sm:gap-4'>
          <div className='relative flex-1 sm:max-w-[415px]'>
            <GradientWrapper>
              <input
                ref={searchInputRef}
                value={searchQuery}
                placeholder='Search by title or description...'
                className='text-foreground bg-input border-border h-[46px] w-full rounded-lg border px-11 py-2 text-sm'
                onChange={e => onSearchChange(e.target.value)}
              />
            </GradientWrapper>

            <Search className='text-primary pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2' />

            {searchQuery.trim().length > 0 && (
              <UiButton
                type='button'
                variant='ghost'
                size='icon'
                className='absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2'
                aria-label='Clear search'
                onClick={() => {
                  onSearchChange('')
                  searchInputRef.current?.focus()
                }}
              >
                <X />
              </UiButton>
            )}
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:gap-3'>
            {isWalletConnected && isModuleAdmin && (
              <UiButton
                variant='white'
                className='h-9 w-full rounded-full sm:w-auto'
                onClick={onMintOpen}
              >
                Mint Shard
                <PlusIcon />
              </UiButton>
            )}
            {isWalletConnected && !isModuleAdmin && !isValidator && (
              <UiButton
                variant='white'
                className='h-9 w-full rounded-full sm:w-auto'
                onClick={onBecomeOpen}
              >
                Become Validator
              </UiButton>
            )}
            {isWalletConnected && isValidator && isJailed && (
              <UiTooltipProvider>
                <UiTooltip>
                  <UiTooltipTrigger asChild>
                    <UiButton
                      variant='white'
                      className='h-9 w-full rounded-full sm:w-auto'
                      onClick={onUnjailOpen}
                    >
                      <AlertCircle className='size-4' />
                      Unjail Validator
                    </UiButton>
                  </UiTooltipTrigger>
                  <UiTooltipContent className='max-w-xs'>
                    <p className='text-xs'>
                      Your validator is jailed. Click to send an unjail
                      transaction to restore active status.
                    </p>
                  </UiTooltipContent>
                </UiTooltip>
              </UiTooltipProvider>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default NftListHeader
